import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import * as Tgcc from '../tgccGlobal';
import * as ReactRedux from 'react-redux';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { CSSProperties } from 'react';
import { CatalogInfo } from '../ProofEdit/CatalogInfo';
import { CatalogArtUpload } from '../UploadFiles/CatalogArtUpload';
import { Message } from '../Components/Message';
import { CatalogHeader } from '../Components/CatalogHeader';
import * as Request from '../tgccRequest';
import Radium, { StyleRoot } from 'radium';
import { Formik, Form, Field, FieldArray, ErrorMessage, FormikTouched } from 'formik';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import styled from '@emotion/styled';
import { 
  FaPlus, 
  FaTrash, 
  FaChevronDown, 
  FaUpload, 
  FaCalendarAlt, 
  FaUser, 
  FaTshirt, 
  FaArrowLeft, 
  FaArrowRight,
  FaCheckCircle,
  FaSearch
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

import { CatalogStep1 } from './CatalogComponents/CatalogStep1';
import { ProofConfirmationEmailToggle } from './ProofConfirmationEmailToggle';

const LoadingSpinner = (
  <img 
    style={{ width: '50px', height: '50px', objectFit: 'contain' }} 
    src={Tgcc.ResolveUrl("~/images/loading.gif")} 
    alt="Loading..."
  />
);

// ========== Theme ==========
const theme = {
  colors: {
    primary: '#E27558',
    primaryLight: '#F8E9E5',
    secondary: '#4A6FA5',
    text: '#2D3748',
    textLight: '#718096',
    border: '#E2E8F0',
    background: '#F8FAFC',
    error: '#E53E3E',
    success: '#38A169',
    warning: '#DD6B20',
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
    primary: '0 0 0 3px rgba(226, 117, 88, 0.3)',
  },
  radii: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
  },
};

// ========== Styled Components ==========
const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const ThumbnailContainer = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Thumbnail = styled(motion.img)`
  width: 100px;
  height: 100px;
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
  object-fit: contain;
  background: white;
  box-shadow: ${theme.shadows.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.md};
  }
`;

const ThumbnailPlaceholder = styled(motion.div)`
  width: 100px;
  height: 100px;
  border-radius: ${theme.radii.md};
  border: 1px dashed ${theme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textLight};
  font-size: 0.75rem;
  text-align: center;
  padding: 0.5rem;
  background: white;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Section = styled(motion.section)`
  background: white;
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.lg};
  padding: 2.5rem;
  margin-bottom: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: ${theme.shadows.xl};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${theme.colors.primary};
  }
`;

const Title = styled.h2`
  color: ${theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  svg {
    color: ${theme.colors.primary};
  }
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Input = styled(Field)`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.9375rem;
  transition: all 0.2s;
  background: white;
  box-shadow: ${theme.shadows.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.primary};
  }
  
  &::placeholder {
    color: ${theme.colors.textLight};
    opacity: 0.6;
  }
`;

const Select = styled(Field)`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.9375rem;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1em;
  transition: all 0.2s;
  background-color: white;
  box-shadow: ${theme.shadows.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.primary};
  }
`;

const TextArea = styled(Field)`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.9375rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.2s;
  line-height: 1.5;
  background: white;
  box-shadow: ${theme.shadows.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.primary};
  }
`;

const Error = styled.div`
  color: ${theme.colors.error};
  font-size: 0.75rem;
  margin-top: 0.5rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &::before {
    content: '⚠';
    font-size: 0.875rem;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #d45a3b 100%);
  color: white;
  border: none;
  border-radius: ${theme.radii.md};
  padding: 1rem 1.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(226, 117, 88, 0.2);
  position: relative;
  overflow: hidden;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(226, 117, 88, 0.3);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  svg {
    margin-right: 0.75rem;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 0;
    border-radius: 100%;
    transform: scale(1, 1) translate(-50%);
    transform-origin: 50% 50%;
  }

  &:focus:not(:active)::after {
    animation: ripple 1s ease-out;
  }

  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 0.5;
    }
    100% {
      transform: scale(20, 20);
      opacity: 0;
    }
  }
`;

const SecondaryButton = styled(motion.button)`
  background: white;
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: 0.875rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  margin-right: 0.75rem;
  box-shadow: ${theme.shadows.sm};
  
  &:hover {
    background: #f7fafc;
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const RequestTypeContainer = styled(motion.div)`
  display: flex;
  max-width: 700px;
  justify-content: center;
  margin: 2rem auto;
  margin-top: 10vh;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f5ff 100%);
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.border};
  align-items: center;
`;

const RequestTypeSelect = styled.select`
  flex-grow: 1;
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: 0.75rem;
  font-size: 1rem;
  margin-left: 1rem;
  transition: all 0.2s;
  background: white;
  font-weight: 500;
  box-shadow: ${theme.shadows.sm};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.primary};
  }
`;

const RequestTypeLabel = styled.div`
  font-weight: 700;
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const ApparelItem = styled(motion.div)`
  border-left: 4px solid ${theme.colors.primary};
  border-radius: ${theme.radii.lg};
  padding: 1.75rem;
  margin-bottom: 2rem;
  background: #fffdfc;
  box-shadow: ${theme.shadows.md};
  position: relative;
  overflow: hidden;

  &:nth-of-type(even) {
    background: #f9f7f5;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary} 0%, #d45a3b 100%);
  }
`;

const DesignItem = styled.div`
  border-bottom: 1px dashed ${theme.colors.border};
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #d45a3b 100%);
  color: white;
  width: 100%;
  max-width: 529px;
  height: 56px;
  margin: 2.5rem auto;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 1.0625rem;
  cursor: pointer;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(226, 117, 88, 0.25);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(226, 117, 88, 0.35);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: 0 4px 14px rgba(226, 117, 88, 0.25) !important;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover::before {
    left: 100%;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
`;

const ErrorAlert = styled(motion.div)`
  background: #fff5f5;
  border-left: 4px solid ${theme.colors.error};
  padding: 1.25rem;
  margin-bottom: 2rem;
  border-radius: ${theme.radii.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  div {
    color: ${theme.colors.error};
    font-weight: 600;
    line-height: 1.5;
  }

  &::before {
    content: '⚠';
    font-size: 1.25rem;
  }
`;

const StepIndicatorWrapper = styled(motion.div)`
  position: sticky;
  top: 75px;
  background: white;
  z-index: 5;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const FormStepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  position: relative;
  margin-top: 1.5rem;
`;

const Step = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 0 1.5rem;
  
  &:not(:last-child):after {
    content: '';
    position: absolute;
    top: 20px;
    right: -30px;
    width: 60px;
    height: 2px;
    background: ${props => props.active ? theme.colors.primary : theme.colors.border};
    transition: all 0.3s ease;
  }
`;

const StepNumber = styled.div<{ active?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? theme.colors.primary : theme.colors.border};
  color: ${props => props.active ? 'white' : theme.colors.textLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-bottom: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? `0 4px 8px ${theme.colors.primaryLight}` : 'none'};
  position: relative;
  z-index: 1;
`;

const StepLabel = styled.div<{ active?: boolean }>`
  font-size: 0.875rem;
  font-weight: ${props => props.active ? '700' : '500'};
  color: ${props => props.active ? theme.colors.text : theme.colors.textLight};
  transition: all 0.3s ease;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: ${theme.spacing.md};
`;

const DesignCodeInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DesignPreviewContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const DesignPreviewLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: ${theme.radii.md};
  background: ${theme.colors.background};
`;

const SuccessBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: ${theme.colors.success};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.full};
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: ${theme.spacing.sm};
`;

// ========== Interfaces ==========
interface Design {
  position: 'LC' | 'BACK' | 'FRONT' | '';
  code: string;
  notes: string;
}

interface Apparel {
  style: string;
  color: string;
  designs: Design[];
}

interface FormValues { 
  apparel: Apparel[]; 
  notes: string;
  previousPurchases: string;
}

// ========== Validation Schema ==========
// 1) Catalog‐mode designs:
const designSchema = Yup.object({
  position: Yup
    .string()
    .oneOf(['LC','BACK','FRONT'], 'Please select a valid position'),
  code:    Yup.string(),      // ← now totally optional
  notes:   Yup.string(),
});



export const getValidationSchema = (requestType: string) => {
  if (requestType === 'Catalog') {
    return Yup.object({
      notes: Yup.string(),
      apparel: Yup.array().of(
        Yup.object({
          style: Yup.string().required('Style is required'),
          color: Yup.string().required('Color is required'),
          designs: Yup.array().of(designSchema)
        })
      )
    })
    .test(
      'notes-or-apparel',
      'Either fill in a note OR add at least one apparel item with its design',
      function (value) {
        // use empty defaults if anything is undefined
        const notes = value?.notes?.trim() ?? '';
        const apparel = Array.isArray(value?.apparel) ? value.apparel : [];

        // if they wrote a note, we pass
        if (notes) return true;

        // otherwise we require at least one complete item+design
        return apparel.some(item => {
          const style = item?.style?.trim() ?? '';
          const color = item?.color?.trim() ?? '';
          // make sure we have an array
          const designs = Array.isArray(item.designs) ? item.designs : [];
          const hasValidDesign = designs.some(d => {
            const pos = d?.position?.trim() ?? '';
            const code = d?.code?.trim() ?? '';
            return pos !== '' && code !== '';
          });
          return style !== '' && color !== '' && hasValidDesign;
        });
      }
    );
  }
}

const emptySchema = Yup.object().shape({});

const catalogSchema = Yup.object({
  notes:   Yup.string(),
  apparel: Yup.array().of(
    Yup.object({
      style:   Yup.string().required('Style is required'),
      color:   Yup.string().required('Color is required'),
      designs: Yup.array().of(designSchema),
    })
  )
})
.test(
  'notes-or-apparel',
  'Either fill in a note OR add at least one apparel item with its design',
  function (value) {
    const notes    = (value?.notes    ?? '').trim();
    const apparel  = Array.isArray(value?.apparel) ? value.apparel : [];

    if (notes) return true;

    return apparel.some(item => {
      const style   = (item?.style ?? '').trim();
      const color   = (item?.color ?? '').trim();
      const designs = Array.isArray(item.designs) ? item.designs : [];
      // "valid" means both position+code are set—only check those that the user actually filled
      return (
        style !== '' &&
        color !== '' &&
        designs.some(d => (d.position?.trim() ?? '') !== '' && (d.code?.trim() ?? '') !== '')
      );
    });
  }
);

// 3) (Optional) A Website schema if you need one, or else you can reuse emptySchema:
const websiteSchema = emptySchema;

const pickSchema = (requestType: string) => {
  switch (requestType) {
    case 'Catalog':
      return catalogSchema;
    case 'Website':
      return websiteSchema;
    default:
      return emptySchema;
  }
};

// ========== Helper Functions ==========
const isBlankOrEmpty = (str: string) => {
  if(!str) return true;
  if(!str.trim()) return true;
  return false;
}

// ========== Animation Variants ==========
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } }
};

const slideUp = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
};

// ========== Main Component ==========
export const Catalog = () => {
  const dispatch = useDispatch();
  const state = useSelector((state: Tgcc.GlobalState) => state);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isWaiting, setIsWaiting] = useState(false);
  const [proofId, setProofId] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [finished, setFinished] = useState(false);
  const [requestType, setRequestType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [designThumbnails, setDesignThumbnails] = useState<Record<string, string>>({});
  const [loadingDesigns, setLoadingDesigns] = useState<Record<string, boolean>>({});
  

  useEffect(() => {
    setUploadedFiles([]);

    if (!state.ActiveProof) {
      const newProof = new Tgcc.Proof();
      newProof.phone = state.UserInfo.phone;
      newProof.smsAllowed = state.UserInfo.smsAllowed;
      newProof.notes = "";
      dispatch({ type: Tgcc.StateAction.SetActiveProof, payload: newProof });
    }
  }, []);

  useEffect(() => {
    if (state?.ActiveProof?.apparelItems) {
      state.ActiveProof.apparelItems.forEach(apparelItem => {
        apparelItem.designs.forEach(design => {
          const trimmedCode = design.code?.trim();
          if (trimmedCode && !designThumbnails[trimmedCode]) {
            fetchDesign(trimmedCode);
          }
        });
      });
    }
  }, [state?.ActiveProof?.apparelItems]);

  const openDesignSelector = (apparelIndex: number, designIndex: number) => {
    dispatch({
      type: Tgcc.StateAction.SetCurrentDesignSelection,
      payload: { apparelIndex, designIndex },
    });
  
    // Let Redux update first
    setTimeout(() => {
      browserHistory.push(Tgcc.Routes.CatalogDesignListView);
    }, 0);
  };
  
  

  const fetchDesign = (designCode: string) => {
    if (!designCode || designThumbnails[designCode]) return;
  
    setLoadingDesigns(prev => ({
      ...prev,
      [designCode]: true
    }));
  
    Request.getDesign(designCode, 
      (design: Tgcc.Design) => {
        setDesignThumbnails(prev => ({
          ...prev,
          [designCode]: Tgcc.getDesignPath(design.thumbnail)
        }));
        setLoadingDesigns(prev => ({
          ...prev,
          [designCode]: false
        }));
      },
      (err: string) => {
        console.error('Error loading design:', err);
        setLoadingDesigns(prev => ({
          ...prev,
          [designCode]: false
        }));
      }
    );
  };

  const setErrorMessage = (msg: string) => {
    setError(msg);
    setIsWaiting(false);
    return false;
  };

  const FormSync = () => {
    const { values } = useFormikContext<FormValues>();
    const dispatch = useDispatch();
    const activeProof = useSelector((s: Tgcc.GlobalState) => s.ActiveProof, shallowEqual);
  
    useEffect(() => {
      // safely grab an array (might be undefined on first mount)
      const proofItems = activeProof?.apparelItems ?? [];
  
      const mappedProofItems = proofItems.map(item => ({
        style:   item.style,
        color:   item.color,
        designs: item.designs.map(d => ({
          position: d.position,
          code:     d.code,
          notes:    d.notes,
        })),
      }));
  
      const formProofItems = values.apparel.map(item => ({
        style:   item.style,
        color:   item.color,
        designs: item.designs.map(d => ({
          position: d.position as 'LC' | 'BACK' | 'FRONT' | '',
          code:     d.code,
          notes:    d.notes,
        })),
      }));
  
      const itemsChanged = JSON.stringify(formProofItems) !== JSON.stringify(mappedProofItems);
      const notesChanged = values.notes !== (activeProof?.notes ?? '');
  
      if (itemsChanged || notesChanged) {
        dispatch({
          type: Tgcc.StateAction.SetActiveProof,
          payload: {
            // if activeProof was undefined, start with an empty Proof
            ...(activeProof ?? new Tgcc.Proof()),
            apparelItems: formProofItems,
            notes:        values.notes,
          },
        });
      }
    }, [
      JSON.stringify(values.apparel),
      values.notes,
      dispatch,
      // watch for proofItems or proof.notes changing in store
      activeProof?.apparelItems,
      activeProof?.notes,
    ]);
  
    return null;
  };
  
  
  

  const initialValues: FormValues = {
    apparel: (state.ActiveProof?.apparelItems || []).length > 0
      ? state.ActiveProof.apparelItems.map(item => ({
          style: item.style || '',
          color: item.color || '',
          designs: (item.designs || []).map(design => ({
            position: ['LC', 'BACK', 'FRONT'].includes(design.position)
              ? (design.position as 'LC' | 'BACK' | 'FRONT')
              : '',
            code: design.code || '',
            notes: design.notes || '',
          })),
        }))
      : [{ style: '', color: '', designs: [{ position: '', code: '', notes: '' }] }],
    previousPurchases: state.ActiveProof?.previousPurchases || '',
    notes: state.ActiveProof?.notes || '',
  };
  
  

  const validatePasswords = () => {
    if (!password || !password.trim())
      return setErrorMessage('Password cannot be blank');
    if (!confirmPassword)
      return setErrorMessage('Passwords do not match');
    if (password.trim() !== confirmPassword.trim())
      return setErrorMessage('Passwords do not match');
    return true;
  };

  const registerNewUser = (onSuccess: (login: Tgcc.LoginData, user: Tgcc.UserInfo) => void) => {
    if (!state.UserInfo) return;
  
    const user = state.UserInfo;
    
    if (!user.firstName?.trim())
      return setErrorMessage('First Name cannot be blank');
    if (!user.lastName?.trim())
      return setErrorMessage('Last Name cannot be blank');
    if (!user.email?.trim())
      return setErrorMessage('Email cannot be blank');
    if (!password?.trim())
      return setErrorMessage('Password cannot be blank');
    if (password !== confirmPassword)
      return setErrorMessage('Passwords do not match');
  
    setIsWaiting(true);
  
    Request.registerUser(user, password, confirmPassword,
      (newUser: Tgcc.UserInfo) => {
        Request.loginUser(user.email, password,
          (ld: Tgcc.LoginData) => {
            dispatch({ type: Tgcc.StateAction.Login, payload: ld });
            dispatch({ type: Tgcc.StateAction.SetUserInfo, payload: user });
            onSuccess(ld, user);
          },
          (err: string) => {}
        );
      },
      setErrorMessage
    );
  };

  const saveCatalogRequest = async (loginData: Tgcc.LoginData, proof: Tgcc.Proof) => {
    setIsWaiting(true);
    try {
      const created = await Request.createCatalogRequest(loginData.username, loginData, proof);
      proofComplete(created);
    } catch (errMsg) {
      setError(errMsg as string);
      setIsWaiting(false);
    }
  };
  
  
  const proofComplete = (proof: Tgcc.Proof) => {
    setFinished(true);
    setProofId(proof.proofId);
  };

  const updateUserInfo = (user: Tgcc.UserInfo) => {
    if (!state.LoginData) return;
  
    Request.updateUser(state.LoginData, user,
      (userData: Tgcc.UserInfo) => {
        dispatch({ type: Tgcc.StateAction.SetUserInfo, payload: userData });
      },
      () => {}
    );
  };

  const successfulRegistration = (login: Tgcc.LoginData, uData: Tgcc.UserInfo) => {
    Tgcc.successfulLogin(dispatch, login);
    updateUserInfo(uData);
    saveCatalogRequest(login, state.ActiveProof);
  };
  
  const formatDate = (date: Date, days: number) => {
    const monthNames = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    const day = date.getDate() + days;
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    return `${year}-${monthNames[monthIndex]}-${day}`;
  };
  
  const formatInHandsDate = (date: Date) => {
    const monthNames = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    return `${year}-${monthNames[monthIndex]}-${day}`;
  };

  const validateUser = () => {
    if (!state.UserInfo)
      return setErrorMessage("Please login or register");
  
    const user = state.UserInfo;
    
    // Check if CSR is impersonating - if so, bypass profile requirements
    const isImpersonating = state.LoginData && state.LoginData.impersonate;
    
    if (isImpersonating) {
      // Only validate email for impersonated users
      if (isBlankOrEmpty(user.email))
        return setErrorMessage("Email address cannot be null");
      if (!Tgcc.IsValidEmail(user.email))
        return setErrorMessage("Email is not valid");
      return true;
    }
    
    // Full validation for regular users
    if (isBlankOrEmpty(user.email || user.firstName || user.lastName || user.phone))
      return setErrorMessage("Please login or register");
    if (isBlankOrEmpty(user.email))
      return setErrorMessage("Email address cannot be null");
    if (!Tgcc.IsValidEmail(user.email))
      return setErrorMessage("Email is not valid");
    if (isBlankOrEmpty(user.firstName) || isBlankOrEmpty(user.lastName))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your first name and last name.");
    if (isBlankOrEmpty(user.phone))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your phone number.");
    if (isBlankOrEmpty(user.address))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your address.");
    if (isBlankOrEmpty(user.city))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your city.");
    if (isBlankOrEmpty(user.state))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your state.");
    if (isBlankOrEmpty(user.zip))
      return setErrorMessage("Please complete your profile information. Go to your Dashboard to update your zip code.");
    if (!Tgcc.IsValidPhoneNumber(user.phone))
      return setErrorMessage("Please enter a valid phone number. Go to your Dashboard to update your phone number.");
  
    return true;
  };
  
  const validateDate = () => {
    if (isBlankOrEmpty(state.ActiveProof?.inHandsDate)) {
      return setErrorMessage("In Hands Date cannot be blank");
    }
    return true;
  };
  
  const onChangeRequest = (evt: React.SyntheticEvent<HTMLSelectElement>) => {
    const value = evt.currentTarget.value;
    setRequestType(value);
  };
  
  const onSubmit = async (values: FormValues) => {
    // clear old error and start spinner
    setError('');
    setIsSubmitting(true);
  
    // ─── 1) Client-side validations ───
    if (!requestType) {
      setError('Please select a Request Type (Catalog or Website) before submitting.');
      setIsSubmitting(false);
      return;
    }
    if (!state.UserInfo) {
      setError('Please log in or register.');
      setIsSubmitting(false);
      return;
    }
    if (!validateDate()) {
      // validateDate calls setError internally
      setIsSubmitting(false);
      return;
    }
    if (!validateUser()) {
      // validateUser calls setError internally
      setIsSubmitting(false);
      return;
    }
  
    try {
      // ─── 2) Handle registration vs login ───
      let loginData: Tgcc.LoginData;
      if (!state.LoginData) {
        // password check
        if (!validatePasswords()) {
          setIsSubmitting(false);
          return;
        }
  
        // wrap registerNewUser in a Promise
        loginData = await new Promise<Tgcc.LoginData>((resolve, reject) => {
          // call it with a single onSuccess callback
          const maybeFalse = registerNewUser((ld, ui) => {
            resolve(ld);
          });
          // if it returned false, that was a validation error
          if (maybeFalse === false) {
            // registerNewUser already called setError(...)
            reject(error || 'Registration failed');
          }
        });
      } else {
        loginData = state.LoginData;
      }
  
      // ─── 3) Submit to server ───
      await handleFormSubmission(loginData, values);
  
    } catch (errMsg) {
      // any thrown string or validation rejection
      setError(errMsg as string);
      setIsSubmitting(false);
    }
  };
  

  const handleFormSubmission = async (
    loginData: Tgcc.LoginData,
    values: FormValues
  ): Promise<void> => {
    // grab user & activeProof from your Redux state
    const user = state.UserInfo!;
    const activeProof = state.ActiveProof!;
  
    // ─── 1) Update user's proof confirmation email preference ───
    try {
      // Get the current preference from the toggle component
      const preferenceToggle = document.querySelector('#proofConfirmationEmails') as HTMLInputElement;
      if (preferenceToggle) {
        const enabled = preferenceToggle.checked;
        await new Promise<void>((resolve, reject) => {
          Request.updateProofConfirmationEmailPreference(
            loginData,
            user.email,
            enabled,
            () => {
              // Update the UserInfo in Redux state
              const updatedUserInfo = {
                ...user,
                proofConfirmationEmailsEnabled: enabled
              };
              dispatch({
                type: Tgcc.StateAction.SetUserInfo,
                payload: updatedUserInfo
              });
              resolve();
            },
            (err) => {
              console.error('Failed to update proof confirmation email preference:', err);
              reject(err);
            }
          );
        });
      }
    } catch (error) {
      console.error('Failed to update preference:', error);
      // Continue with proof submission even if preference update fails
    }
  
    // ─── 2) Build the Proof object ───
    const proof = new Tgcc.Proof();
    proof.proofId        = activeProof.proofId;
    proof.phone          = user.phone;
    proof.smsAllowed     = user.smsAllowed;
    proof.inHandsDate    = activeProof.inHandsDate;
    proof.userEmail      = user.email;
    proof.eventName      = activeProof.eventName || '';
    proof.notes          = values.notes || '';
    proof.previousPurchases = values.previousPurchases || '';
    proof.uploadedFiles  = activeProof.uploadedFiles || '';
    proof.division       = activeProof.division || '';
    proof.salescontact   = activeProof.salescontact || '';
    proof.frontLocation  = activeProof.frontLocation || 'None';
    proof.backLocation   = activeProof.backLocation || 'None';
    proof.embroidery     = activeProof.embroidery || false;
    proof.screenPrint    = activeProof.screenPrint || false;
    proof.timeSensitive  = activeProof.timeSensitive || false;
  
    if (requestType === 'Website') {
      proof.status      = 'WEBSITE REQUEST';
      proof.requesttype = 'Website';
      (proof as any).designs = values.apparel.flatMap((item: any) => item.designs);
    } else {
      proof.status      = 'CATALOG PENDING';
      proof.requesttype = 'Catalog';
      (proof as any).apparelItems = values.apparel;
    }
  
    // ─── 3) Send to the server ───
    try {
      const createdProof = await Request.createCatalogRequest(
        loginData.username,
        loginData,
        proof
      );
      // ─── 4) On success, update UI
      proofComplete(createdProof);
    } catch (errMsg) {
      // any network or JSON error comes here
      throw errMsg;
    }
  };
  
  const whenFinished = () => {
    dispatch({ type: Tgcc.StateAction.SetActiveProof, payload: new Tgcc.Proof() });
    window.location.reload();
  };

  const getThankYou = () => (
    <Message
      Title="Thank You!"
      UseTitleScript={true}
      Message1="Your catalog request has been received."
      Message2={`Proof Number: ${proofId}`}
      onContinue={whenFinished}
    />
  );

  const handleSetActiveStep = (step: number) => {
    setActiveStep(step);
    };
    
    const nextStep = () => {
    setActiveStep(prev => Math.min(prev + 1, 4));
    };
    
    const prevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 1));
    };

    function flattenErrors(err: any): string[] {
      if (typeof err === 'string') {
        return [err];
      }
      if (Array.isArray(err)) {
        return err.flatMap(item => flattenErrors(item));
      }
      if (typeof err === 'object' && err !== null) {
        return Object.values(err).flatMap(item => flattenErrors(item));
      }
      return [];
    }
    
    
    const renderStepContent = (values: any, setFieldValue: any) => {
    const isWebsiteRequest = requestType === "Website";
    switch (activeStep) {
      case 1:
        return (
          <CatalogStep1
            requestType={requestType}
            fetchDesign={fetchDesign}
            designThumbnails={designThumbnails}
            loadingDesigns={loadingDesigns}
            openDesignSelector={openDesignSelector}
            nextStep={nextStep}
          />
        );
        case 2:
          return (
            <Section variants={sectionVariants} initial="hidden" animate="visible">
              <Title>
                <FaCalendarAlt /> Order Information
              </Title>
              <CatalogInfo />
              <NavigationButtons>
                <SecondaryButton type="button" onClick={prevStep} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <FaArrowLeft style={{ marginRight: '0.75rem' }} /> Back
                </SecondaryButton>
                <PrimaryButton type="button" onClick={nextStep} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Next <FaArrowRight style={{ marginLeft: '0.75rem' }} />
                </PrimaryButton>
              </NavigationButtons>
            </Section>
          );
        case 3:
          return (
            <Section variants={sectionVariants} initial="hidden" animate="visible">
              <Title>
                <FaUpload /> Artwork Upload
              </Title>
              <CatalogArtUpload />
              <NavigationButtons>
                <SecondaryButton type="button" onClick={prevStep} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <FaArrowLeft style={{ marginRight: '0.75rem' }} /> Back
                </SecondaryButton>
                <PrimaryButton type="button" onClick={nextStep} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  Next <FaArrowRight style={{ marginLeft: '0.75rem' }} />
                </PrimaryButton>
              </NavigationButtons>
            </Section>
          );
        case 4:
          return (
            <Section variants={sectionVariants} initial="hidden" animate="visible">
              <Title>
                <FaUser /> Review & Submit
              </Title>
              <p style={{ color: theme.colors.text, marginBottom: '1.5rem' }}>
                Please review all the information you've entered before submitting your request.
              </p>
      
              {error && (
                  <ErrorAlert initial="hidden" animate="visible">
                    {error}
                  </ErrorAlert>
                )}

              <ProofConfirmationEmailToggle 
                state={state} 
                dispatch={dispatch}
              />
      
              <NavigationButtons>
                <SecondaryButton type="button" onClick={prevStep} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <FaArrowLeft style={{ marginRight: '0.75rem' }} /> Back
                </SecondaryButton>
                <SubmitButton
                  type="submit"
                  disabled={!requestType || isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      {LoadingSpinner}
                      Submitting...
                    </>
                  ) : 'Submit Request'}
                </SubmitButton>
              </NavigationButtons>
            </Section>
          );
        default:
          return null;
      }
    };

      return (
        <Container id="proof">
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CatalogHeader />
        </div>
        <StepIndicatorWrapper>
      <FormStepIndicator>
        <Step active={activeStep === 1}>
          <StepNumber active={activeStep === 1}>1</StepNumber>
          <StepLabel active={activeStep === 1}>Request Details</StepLabel>
        </Step>
        <Step active={activeStep === 2}>
          <StepNumber active={activeStep === 2}>2</StepNumber>
          <StepLabel active={activeStep === 2}>Order Info</StepLabel>
        </Step>
        <Step active={activeStep === 3}>
          <StepNumber active={activeStep === 3}>3</StepNumber>
          <StepLabel active={activeStep === 3}>Artwork</StepLabel>
        </Step>
        <Step active={activeStep === 4}>
          <StepNumber active={activeStep === 4}>4</StepNumber>
          <StepLabel active={activeStep === 4}>Review & Submit</StepLabel>
        </Step>
      </FormStepIndicator>
    </StepIndicatorWrapper>

    <RequestTypeContainer
      initial="hidden"
      animate="visible"
      transition={{ delay: 0.2 }}
    >
      <RequestTypeLabel>Request Type:</RequestTypeLabel>
      <RequestTypeSelect 
        onChange={onChangeRequest} 
        value={requestType}
        required
      >
        <option value="">Select an option</option>
        <option value="Catalog">Catalog Request</option>
        <option value="Website">Website Request</option>
      </RequestTypeSelect>
    </RequestTypeContainer>

    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={pickSchema(requestType)}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
    >
  {formik => {
    // expose for console debugging
    // @ts-ignore
    (window as any).formik = formik;

    // install the JSON‑import helper once
    React.useEffect(() => {
      (window as any).importJsonToFormik = () => {
        const raw = window.prompt("📋 Paste your JSON here:");
        if (!raw) {
          console.warn("Import cancelled");
          return;
        }
        // sanitize actual newlines
        const sanitized = raw.replace(/\\?\\n/g, "\\n");
        let data: Record<string, any>;
        try {
          data = JSON.parse(sanitized);
        } catch (err) {
          console.error("Invalid JSON:", err);
          return;
        }
        // helper to set nested paths
        const setNested = (obj: any, path: string, val: any) => {
          const keys = path.split(".");
          let cur = obj;
          keys.forEach((k, i) => {
            const last = i === keys.length - 1;
            const key = isNaN(+k) ? k : +k;
            if (last) {
              cur[key] = val;
            } else {
              if (cur[key] == null) {
                cur[key] = isNaN(+keys[i + 1]) ? {} : [];
              }
              cur = cur[key];
            }
          });
        };
        // build new values
        const newValues = { ...formik.values };
        Object.entries(data).forEach(([path, val]) => {
          setNested(newValues, path, val);
        });
        formik.setValues(newValues);
        console.log("✅ Formik values imported from JSON");
      };
    }, [formik]);

    const {
      values,
      errors,
      touched,
      submitCount,
      setTouched,
      setFieldValue
    } = formik;

    // Flood‑mark touched on submit
    React.useEffect(() => {
      if (submitCount > 0) {
        const allTouched: FormikTouched<FormValues> = {
          notes: true,
          apparel: values.apparel.map(apparelItem => ({
            style: true,
            color: true,
            designs: apparelItem.designs.map(() => ({
              position: true,
              code: true,
              notes: true,
            })),
          })),
        };
        setTouched(allTouched, true);
      }
    }, [submitCount, values.apparel, setTouched]);

    return (
      <>
        {/* Button to trigger the import prompt */}
        <PrimaryButton
          type="button"
          onClick={() => (window as any).importJsonToFormik()}
          style={{ marginBottom: '1rem' }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FaUpload /> Import JSON
        </PrimaryButton>

        <Form>
          {/* Validation summary on Step 4 */}
          {activeStep === 4 && Object.keys(errors).length > 0 && (
            <ErrorAlert initial="hidden" animate="visible">
              <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                {flattenErrors(errors).map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </ErrorAlert>
          )}

          <FormSync /> 
          {/* Render the current step's content */}
          {renderStepContent(values, setFieldValue)}

          {/* Show thank‑you message once finished */}
          {finished && getThankYou()}
        </Form>
      </>
    );
  }}
</Formik>




  </Container>
  );
}